import React, { useRef } from 'react'
import { Link, useHistory } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBoxArchive, faCheck, faGear, faRocketLaunch } from '@fortawesome/pro-solid-svg-icons'
import { Pill } from '../../../../shared/sso-workspace/pill'
import { agoText, dateTimeText } from '../../../../shared/date/local-time'
import { categoryLabel } from '../../../../shared/notify/words'
import { InboxVisual } from './inbox-visual'
import { InboxRowMenu } from './inbox-row-menu'

export const PREFERENCES_PATH = '/org/notifications/preferences'

// Where an item's link (C-5: always edxr://) goes in the admin: a course
// opens through the launch bridge, the one way into a course from the web.
// Nothing else maps to a page here, so nothing else is offered as a link.
export const linkHref = link => {
  if (!link || typeof link !== 'string') return null
  const m = /^edxr:\/\/content\?id=([1-9]\d{0,17})$/.exec(link.trim())
  return m ? `/launch/content/${m[1]}` : null
}

// How long after the ⋯ menu closed a click on the row is the close's own.
export const MENU_CLOSE_GRACE_MS = 400

const capitalize = text => (text ? text.charAt(0).toUpperCase() + text.slice(1) : text)

// A row's DOM ids from the item's id (a UUID; anything else is made safe).
const idFor = id => String(id).replace(/[^a-zA-Z0-9_-]/g, '')

const Icon = icon => () => <FontAwesomeIcon icon={icon} />
const IconRead = Icon(faCheck)
const IconArchive = Icon(faBoxArchive)
const IconLaunch = Icon(faRocketLaunch)
const IconPreferences = Icon(faGear)

// A row's icon button. Unavailable (while impersonating, or while its
// action runs) it stays focusable with aria-disabled -- never leaving the tab
// order -- and its reason on hover; a press does nothing.
const QuickButton = ({ label, titleID, off, reason, onClick, children }) =>
  <button type="button" className="btn nt-icon-btn" aria-label={label} title={reason || label} aria-describedby={titleID}
    aria-disabled={off ? 'true' : undefined}
    onClick={e => {
      e.stopPropagation()
      if (!off) onClick()
    }}>
    {children}
  </button>

// One notification (owner 2026-10-24: "should the cells be different colors
// for unread vs read?"). Unread: a tinted row, a bold title and a dot (and
// "Unread" for a screen reader); read: plain. The leading picture
// (inbox-visual.js), the title, the body, then when and what kind. One
// primary action stays visible when there is one (Launch); marking read and
// archiving are icon buttons that show on hover or when focus is in the row
// (always in the tab order), and the same actions -- with the preferences --
// are in the ⋯ menu, which a touch screen always shows.
//
// Clicking the row opens its link and marks it read (the title is the link,
// stretched over the row; the buttons sit above it). A row with no link
// marks itself read on a click; from the keyboard, Mark as read does it.
// A click that ends a text selection does nothing.
export const InboxRow = ({ item, now, canWrite, readOnlyReason, busy, onRead, onArchive }) => {
  const history = useHistory()
  const href = linkHref(item.link)
  const unread = !item.read
  const archived = !!item.archived
  const rowID = idFor(item.id)
  const titleID = `nt-title-${rowID}`
  const readBusy = busy === `read:${item.id}`
  const archiveBusy = busy === `archive:${item.id}`
  const canRead = unread && !archived
  const opened = () => {
    if (unread && canWrite) onRead()
  }
  const unreadLabel = unread ? <span className="visually-hidden">Unread: </span> : null

  // A click that only closed this row's open ⋯ menu is not a click on the
  // row: the menu closes on the press (outside it), and the click that
  // follows lands on the row.
  const menuClosedAt = useRef(0)

  const justClosedMenu = () => Date.now() - menuClosedAt.current < MENU_CLOSE_GRACE_MS

  // The title's link covers the row, so a click anywhere lands here first.
  const onTitleClick = e => {
    if (justClosedMenu()) {
      e.preventDefault()
      return
    }
    opened()
  }

  const onRowClick = e => {
    // A click in the ⋯ menu (portalled, so outside this element) or on any
    // control in the row is that control's.
    if (!e.currentTarget.contains(e.target)) return
    if (justClosedMenu()) return
    if (e.target.closest && e.target.closest('a, button, input, select, textarea, [role="menu"]')) return
    const selection = typeof window.getSelection === 'function' ? window.getSelection() : null
    if (selection && String(selection).length > 0) return
    opened()
    if (href) history.push(href)
  }

  const menuItems = [
    href && { key: 'launch', label: 'Launch course', Icon: IconLaunch, onSelect: () => { opened(); history.push(href) } },
    canRead && { key: 'read', label: 'Mark as read', Icon: IconRead, onSelect: onRead, disabled: !canWrite || readBusy, reason: canWrite ? undefined : readOnlyReason },
    !archived && { key: 'archive', label: 'Archive', Icon: IconArchive, onSelect: onArchive, disabled: !canWrite || archiveBusy, reason: canWrite ? undefined : readOnlyReason },
    { key: 'preferences', label: 'Notification preferences', Icon: IconPreferences, onSelect: () => history.push(PREFERENCES_PATH) }
  ].filter(Boolean)

  const classes = [
    'nt-row',
    unread ? 'is-unread' : 'is-read',
    href || (unread && canWrite) ? 'is-clickable' : '',
    item.priority === 'critical' ? 'is-urgent' : ''
  ].filter(Boolean).join(' ')

  return (
    <li className={classes} data-testid={`inbox-item-${item.id}`} data-row-id={item.id} onClick={onRowClick}>
      {unread && <span className="nt-unread-dot" aria-hidden="true" data-testid="unread-dot" />}
      <InboxVisual item={item} />
      <div className="nt-row-main">
        <div className="nt-row-title" id={titleID}>
          {href
            ? <Link className="nt-title nt-stretch" to={href} onClick={onTitleClick}>{unreadLabel}{item.title}</Link>
            : <span className="nt-title">{unreadLabel}{item.title}</span>}
        </div>
        {item.body && <p className="nt-row-body">{item.body}</p>}
        <div className="nt-row-meta">
          <time dateTime={item.createdAt} title={dateTimeText(item.createdAt)}>{capitalize(agoText(item.createdAt, { now }))}</time>
          {item.category && <span className="nt-meta-sep">{categoryLabel(item.category)}</span>}
          {item.count > 1 && <span className="nt-meta-sep">{`${item.count} updates`}</span>}
          {item.priority === 'critical' && <Pill tone="bad">Urgent</Pill>}
        </div>
      </div>
      <div className="nt-row-actions">
        {href &&
          <Link className="btn btn-sm sw-btn-primary nt-launch" to={href} onClick={opened} aria-describedby={titleID}>
            <IconLaunch /> Launch
          </Link>}
        <div className="nt-quick">
          {canRead &&
            <QuickButton label="Mark as read" titleID={titleID} off={!canWrite || readBusy} reason={canWrite ? null : readOnlyReason} onClick={onRead}>
              <IconRead />
            </QuickButton>}
          {!archived &&
            <QuickButton label="Archive" titleID={titleID} off={!canWrite || archiveBusy} reason={canWrite ? null : readOnlyReason} onClick={onArchive}>
              <IconArchive />
            </QuickButton>}
        </div>
        <InboxRowMenu label={`More actions: ${item.title}`} items={menuItems}
          onOpenChange={open => {
            if (!open) menuClosedAt.current = Date.now()
          }} />
      </div>
    </li>
  )
}
